import React, {useState, useEffect, useContext, useCallback} from 'react';
import {LanguageContext} from './languageContext';
import {FormLabels} from '../../containers/layouts/routesConfig';
import {
  JsonToMenu,
  JsonToMenuLevel,
  jsonToTranslate,
} from '../../api/translate';
import {useDispatch, useSelector} from 'react-redux';
import AppContextPropsType from 'types/AppContextPropsType';
import {AppContext} from '@crema';
import {
  Write,
  ActiveRole as ActiveRoleKey,
  Read,
  Token as TokenKey,
} from 'utils/localstorage';
import {getCreditAmount} from '../../redux/actions';
import {PAGE_ID} from './Enum_PageID';
import GifLoading from '@shared/gifLoading';

const LanguageContextProvider: React.FC<{}> = ({children}) => {
  const user = useSelector((store: any) => store.auth);
  let route: FormLabels[] = [];
  const [allMenu, setAllMenu] = useState<any[]>([]);
  const [activeRoleId, setActiveRoleId] = useState();
  const [translation, setTranslation] = useState(route);
  const [menu, setMenu] = useState<any[]>([]);
  const {locale} = useContext<AppContextPropsType>(AppContext);
  const dispatch = useDispatch();
  const token = Read(TokenKey);

  const translate = useCallback(
    (formid: string | any, id: string): {name: string; hasAccess: boolean} => {
      const formId = parseInt(formid); //formid from url
      const form = translation?.find((f) => f.id === formId);
      if (form) {
        const index = form.labels.findIndex((x) => x.id === id);
        if (index >= 0)
          return {
            name: form.labels[index].name,
            hasAccess: !!form?.labels?.[index]?.hasAccess ?? false,
          };
        else return {name: id, hasAccess: false};
      }
      return {name: id, hasAccess: false};
    },
    [translation],
  );

  const updateCreditAmount = useCallback(() => {
    dispatch(getCreditAmount(activeRoleId, setActiveRoleId));
  }, [activeRoleId, dispatch]);

  const handleActiveRoleIDChange = useCallback(
    (newId: any) => {
      dispatch(getCreditAmount(newId, setActiveRoleId));
    },
    [dispatch],
  );

  useEffect(() => {
    if (user?.user?.menu) {
      const activeRoleId = user.user.menu.find(
        (role: any) => role.IsDefaultRole,
      )?.UserDetailID;
      if (activeRoleId) {
        handleActiveRoleIDChange(activeRoleId);
      } else {
        const firstRoleId = user.user.menu?.[0]?.UserDetailID;
        handleActiveRoleIDChange(firstRoleId);
      }

      setAllMenu(user.user.menu);
      const mapMenu = JsonToMenu(
        activeRoleId,
        user.user.menu,
        locale.locale.toUpperCase(),
      );
      setMenu(mapMenu);
      const mapTranslate = jsonToTranslate(
        activeRoleId,
        user.user.menu,
        locale.locale.toUpperCase(),
      );
      setTranslation(mapTranslate);
    }
  }, [user, handleActiveRoleIDChange]);

  useEffect(() => {
    if (user?.user?.menu) {
      const mapTranslate = jsonToTranslate(
        activeRoleId,
        user.user.menu,
        locale.locale.toUpperCase(),
      );
      setTranslation(mapTranslate);
      Write(ActiveRoleKey, activeRoleId);
    }
  }, [locale, activeRoleId, user]);

  const getUserCredit = useCallback(() => {
    if (user?.user?.menu) {
      const temp = user?.user?.menu;
      if (activeRoleId) {
        const result = temp.find(
          (m: any) => m.UserDetailID === activeRoleId,
        )?.Credit;
        if (result) {
          return [result, 'IRR'];
        } else return null;
      } else return null;
    } else return null;
  }, [user, activeRoleId]);

  const translateOptions = useCallback(
    (id: PAGE_ID): ITranslate => {
      return {
        translateLabel: (label: string) => translate(id, label).name,
        translateLabelAccess: (label: string) => translate(id, label).hasAccess,
      };
    },
    [translate],
  );

  return (
    <LanguageContext.Provider
      value={{
        translation,
        allMenu,
        menu,
        locale: locale.locale.toUpperCase(),
        activeRoleId,
        translate,
        setActiveRoleId: handleActiveRoleIDChange,
        getUserCredit,
        translateOptions,
        updateCreditAmount,
      }}>
      {token ? activeRoleId ? children : <GifLoading /> : children}
    </LanguageContext.Provider>
  );
};

//-------------------------------Translate Consumer...
interface ITranslate {
  translateLabel: (arg0: string) => string;
  translateLabelAccess: (arg0: string) => boolean;
}

export const LanguageConsumers = () => useContext(LanguageContext);

export const Translate = (id: PAGE_ID): ITranslate => {
  const language: any = useContext(LanguageContext);
  const translate_ = language.translate;
  return {
    translateLabel: (label: string) => translate_(id, label).name,
    translateLabelAccess: (label: string) => translate_(id, label).hasAccess,
  };
};

//------------------------------Menu Consumer...
export const Menu = () => {
  const {allMenu, activeRoleId, locale} = useContext<any>(LanguageContext);
  return JsonToMenu(activeRoleId, allMenu, locale);
};

export const MenuLevel = () => {
  const {allMenu, activeRoleId, locale} = useContext<any>(LanguageContext);
  return JsonToMenuLevel(activeRoleId, allMenu, locale);
};

//------------------------------Menu Consumer...
export const GetActiveRoleName = () => {
  const {allMenu, activeRoleId} = useContext<any>(LanguageContext);
  return (
    allMenu.find((role: any) => role.UserDetailID === activeRoleId)?.RoleName ??
    'Default'
  );
};

//------------------------------Active Roles Id...
export const ActiveRoleId = () => {
  const {activeRoleId} = useContext<any>(LanguageContext);
  return activeRoleId;
};

export const ActiveRoleOwnerID = () => {
  const {allMenu, activeRoleId} = useContext<any>(LanguageContext);
  return allMenu.find((role: any) => role.UserDetailID === activeRoleId)
    ?.OwnerID;
};

//export provider as default
export default LanguageContextProvider;
export {PAGE_ID};
